import { useState } from "react";
import { useTeamPractice } from "@/lib/teamRecordQueries";
import AssignFromLibraryModal from "../AssignFromLibraryModal";

/**
 * Clients Restructure — Team 360 -> Practice & Layers. Shows only
 * TEAM-owned Assignments (owner_member_id=null) — an individual
 * assignment is created from that member's own Client 360, never here.
 */
export default function PracticeLayersTab({ workspaceId }) {
  const { data, isPending, isError } = useTeamPractice(workspaceId);
  const [showAssign, setShowAssign] = useState(false);
  const active = data?.active || [];
  const completed = data?.completed || [];
  const nextPractice = data?.next_practice;

  return (
    <div data-testid="team-360-practice-tab" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg text-ink">Next Practice</h2>
          <p className="text-sm text-ink-soft" data-testid="team-practice-next">{nextPractice ? nextPractice.title_snapshot : "None assigned yet"}</p>
        </div>
        <button
          onClick={() => setShowAssign(true)}
          className="text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 transition-colors"
          data-testid="team-practice-assign-btn"
        >
          + Assign Practice
        </button>
      </div>

      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="team-practice-loading">Loading practice…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="team-practice-error">Couldn't load team practice.</p>
      ) : (
        <>
          <div>
            <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Active assignments</h3>
            {active.length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="team-practice-active-empty">No active team practice.</p>
            ) : (
              <div className="space-y-2" data-testid="team-practice-active-list">
                {active.map((a) => (
                  <div key={a.asg_id} className="border border-ink/10 rounded-lg p-3 bg-white/50 flex items-center justify-between" data-testid={`team-practice-active-${a.asg_id}`}>
                    <span className="text-sm text-ink">{a.title_snapshot}</span>
                    <span className="text-xs text-ink-soft">{a.done_count}/{a.member_count} completed</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Completed assignments</h3>
            {completed.length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="team-practice-completed-empty">None completed by the whole team yet.</p>
            ) : (
              <div className="space-y-2" data-testid="team-practice-completed-list">
                {completed.map((a) => (
                  <div key={a.asg_id} className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={`team-practice-completed-${a.asg_id}`}>
                    <span className="text-sm text-ink">{a.title_snapshot}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Assignment history</h3>
            {(data?.items || []).length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="team-practice-history-empty">No team practice assigned yet.</p>
            ) : (
              <ol className="space-y-1" data-testid="team-practice-history-list">
                {(data?.items || []).map((a) => (
                  <li key={a.asg_id} className="text-sm text-ink-soft" data-testid={`team-practice-history-${a.asg_id}`}>
                    {a.title_snapshot} — {new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}

      {showAssign && <AssignFromLibraryModal workspaceId={workspaceId} onClose={() => setShowAssign(false)} />}
    </div>
  );
}
